const dataAccessMethods = require('../../dataAccessMethods');
const Blockly = require('blockly');
const baseMdcBlockGroup = require('./_BaseMdcBlockGroup');
const setupAddCodeProperties = require('../../helpers/addCodeProperties');
const convertToBoolean = require('../../helpers/convertToBoolean');
const { config: { timeSeriesControls } } = require('@sstdev/lib_metadata-config');

function getChartHeights() {
    return [
        // medium needs to be first so it is the default selection
        ['Medium', 'medium'],
        ['Tall', 'tall'],
        ['Short', 'short']
    ];
}

class Graph extends baseMdcBlockGroup {
    addCodeGeneratorCustom(code, blocklyBlock, hNodeType) {
        const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);

        addCodeProperties([
            { key: 'liveReload', handler: val => val.toUpperCase() !== 'FALSE' },
            'namespace',
            'relation',
            'filterRelation',
            'chartHeight',
            { key: 'chartWidth', handler: value => value === 'half' ? value : undefined },
            'limitResults'
        ]);
        if (blocklyBlock.type === 'HorizontalChart') {
            addCodeProperties([{ key: 'stackValues', handler: convertToBoolean }]);
        }
        if (hNodeType === 'TimeSeriesChart') {
            addCodeProperties(['timeProperty', 'seriesProperty', 'valueTitle', 'defaultRange', 'defaultGrouping']);
            code.dateRanges = timeSeriesControls.ranges
                .filter(({ value }) => convertToBoolean(blocklyBlock.getFieldValue(`range_${value}`)))
                .map(({ value }) => value);
            code.groupings = timeSeriesControls.groupings
                .filter(({ value }) => convertToBoolean(blocklyBlock.getFieldValue(`grouping_${value}`)))
                .map(({ value }) => value);
            if (!code.dateRanges.includes(code.defaultRange) || !code.groupings.includes(code.defaultGrouping)) {
                throw new Error('Time Series Chart: enable the default date range and grouping in the available choices.');
            }
        }

        return JSON.stringify(code);
    }

    addToBlocksCustom(color, blocklyBlock, hNodeType) {
        // noinspection JSCheckFunctionSignatures // overloaded
        blocklyBlock
            .appendDummyInput()
            .setAlign(Blockly.ALIGN_RIGHT)
            .appendField('  Namespace:')
            .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace');

        let blockId = blocklyBlock.id;
        let workspace = blocklyBlock.workspace;
        // noinspection JSCheckFunctionSignatures // overloaded
        const lineOfFields = blocklyBlock
            .appendDummyInput()
            .setAlign(Blockly.ALIGN_RIGHT)
            .appendField('  Relation:')
            .appendField(
                new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                'relation'
            )
            .appendField('  Chart Height:')
            .appendField(new Blockly.FieldDropdown(getChartHeights()), 'chartHeight')
            .appendField('  Chart Width:')
            .appendField(new Blockly.FieldDropdown([['Standard', 'standard'], ['Half', 'half']]), 'chartWidth')
            .appendField('  Live Reload:')
            .appendField(new Blockly.FieldCheckbox('false'), 'liveReload')
            .appendField('  Limit Results:')
            .appendField(new Blockly.FieldNumber(21, 0), 'limitResults');

        blocklyBlock
            .appendDummyInput()
            .setAlign(Blockly.ALIGN_RIGHT)
            .appendField('  Shared filters from relation (optional):')
            .appendField(
                new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId, true)),
                'filterRelation'
            );

        if (hNodeType === 'HorizontalChart') {
            lineOfFields.appendField('  Stack Values:').appendField(new Blockly.FieldCheckbox(false), 'stackValues');
        }
        if (hNodeType === 'TimeSeriesChart') {
            // Group the source timestamp in the viewer's timezone. A series field
            // adds separate lines without downloading individual records.
            blocklyBlock.appendDummyInput()
                .appendField('Timestamp property:')
                .appendField(new Blockly.FieldTextInput(''), 'timeProperty')
                .appendField('Series property (optional):')
                .appendField(new Blockly.FieldTextInput(''), 'seriesProperty');
            blocklyBlock.appendDummyInput()
                .appendField('Value label:')
                .appendField(new Blockly.FieldTextInput('Records'), 'valueTitle');
            const ranges = blocklyBlock.appendDummyInput().appendField('Date ranges:');
            timeSeriesControls.ranges.forEach(({ value, label }) => {
                ranges.appendField(label).appendField(new Blockly.FieldCheckbox(true), `range_${value}`);
            });
            const groupings = blocklyBlock.appendDummyInput().appendField('Groupings:');
            timeSeriesControls.groupings.forEach(({ value, label }) => {
                groupings.appendField(label).appendField(new Blockly.FieldCheckbox(true), `grouping_${value}`);
            });
            blocklyBlock.appendDummyInput()
                .appendField('Default date range:')
                .appendField(new Blockly.FieldDropdown(timeSeriesControls.ranges.map(({ value, label }) => [label, value])), 'defaultRange')
                .appendField('Default grouping:')
                .appendField(new Blockly.FieldDropdown(timeSeriesControls.groupings.map(({ value, label }) => [label, value])), 'defaultGrouping');
            blocklyBlock.setFieldValue('oneDay', 'defaultRange');
            blocklyBlock.setFieldValue('hour', 'defaultGrouping');
        }

        blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Filters:');

        blocklyBlock
            .appendStatementInput('filterControls')
            .setCheck(['actionElement', 'searchPane'])
            .appendField('Filter Controls:');
    }

    customizeToolbox(section) {
        section.contents.forEach(block => {
            // The time-series controls supply their own date range and grouping.
            if (block.type === 'graph_TimeSeriesChart') return;
            block.inputs = {
                filters: {
                    block: {
                        type: 'filter_GroupBy',
                        fields: { propertyName: 'title' },
                        next: {
                            block: {
                                type: 'filter_DateRange'
                            }
                        }
                    }
                }
            };
        });
    }
}

module.exports = Graph;
